/**
 * Leeftijd bij een gebeurtenis, van akte-tekst naar een geldige GEDCOM-waarde.
 *
 * `event_participants.age` is vrije tekst omdat een akte dat ook is: "27",
 * "27 jaar", "3 maanden", "6 weken oud", "circa 40". GEDCOM 5.5.1 laat in
 * `AGE_AT_EVENT` alleen een vaste vorm toe — `27y`, `3m`, `10d`, combinaties
 * daarvan, of de woorden CHILD / INFANT / STILLBORN.
 *
 * Een kaal getal zonder eenheid is dus géén geldige waarde, en `parseInt` op
 * "3 maanden" levert 3 — waarna een zuigeling in het bestand van de ontvanger
 * als drie jaar oud verschijnt (#1326). Daarom leest deze functie de eenheid
 * mee en laat hij alles los wat hij niet met zekerheid begrijpt: liever geen
 * regel dan een verkeerde leeftijd.
 */

/** Eenheid-woorden uit Nederlandse en Engelse akten. */
const EENHEDEN: Array<{ patroon: RegExp; letter: 'y' | 'm' | 'd' | 'w' }> = [
  { patroon: /^(jaar|jaren|jr|j|year|years|yr|yrs|y)$/, letter: 'y' },
  { patroon: /^(maand|maanden|mnd|mnden|month|months|mo|mos|m)$/, letter: 'm' },
  { patroon: /^(dag|dagen|dg|day|days|d)$/, letter: 'd' },
  { patroon: /^(week|weken|wk|weeks|w)$/, letter: 'w' },
];

/** Woorden die GEDCOM zelf als leeftijd kent. */
const WOORD_LEEFTIJDEN: Record<string, string> = {
  kind: 'CHILD',
  child: 'CHILD',
  zuigeling: 'INFANT',
  baby: 'INFANT',
  infant: 'INFANT',
  levenloos: 'STILLBORN',
  doodgeboren: 'STILLBORN',
  stillborn: 'STILLBORN',
};

function eenheidLetter(woord: string): 'y' | 'm' | 'd' | 'w' | null {
  const schoon = woord.toLowerCase().replace(/[.,]/g, '').trim();
  for (const { patroon, letter } of EENHEDEN) {
    if (patroon.test(schoon)) return letter;
  }
  return null;
}

/**
 * @param ruw - de waarde uit `event_participants.age`
 * @returns een geldige AGE_AT_EVENT, of null wanneer de tekst niet met
 *   zekerheid te lezen is
 */
export function naarGedcomLeeftijd(ruw: unknown): string | null {
  if (ruw === null || ruw === undefined) return null;
  const tekst = String(ruw).trim().toLowerCase();
  if (!tekst) return null;

  if (WOORD_LEEFTIJDEN[tekst]) return WOORD_LEEFTIJDEN[tekst];

  // "circa 40", "ongeveer 40", "±40", "ca. 40" — GEDCOM kent geen "ongeveer"
  // bij een leeftijd, maar `>` en `<` wel. Een schatting is geen ondergrens,
  // dus die betekenis mogen we er niet aan geven: de waarde gaat mee zonder
  // het voorbehoud, want het jaartal is bruikbaarder dan niets.
  const zonderVoorbehoud = tekst.replace(
    /^(circa|ca\.?|ongeveer|ong\.?|omstreeks|±|~|about|abt\.?)\s*/,
    ''
  );

  // Al in GEDCOM-vorm: "27y", "1y 3m", "10d"
  if (/^(\d+y)?\s*(\d+m)?\s*(\d+d)?$/.test(zonderVoorbehoud) && /\d/.test(zonderVoorbehoud)) {
    return zonderVoorbehoud.replace(/\s+/g, ' ').trim();
  }

  // Een kaal getal is in een akte altijd een aantal jaren.
  if (/^\d{1,3}$/.test(zonderVoorbehoud)) return `${parseInt(zonderVoorbehoud, 10)}y`;

  // "27 jaar", "3 maanden", "1 jaar 3 maanden", "6 weken"
  const paren = [...zonderVoorbehoud.matchAll(/(\d{1,4})\s*([a-zé]+)/g)];
  if (paren.length === 0) return null;

  const delen: { y?: number; m?: number; d?: number } = {};
  for (const [, getal, woord] of paren) {
    const letter = eenheidLetter(woord);
    if (!letter) return null; // een woord dat we niet kennen: niets gokken
    const aantal = parseInt(getal, 10);
    if (letter === 'w') {
      // GEDCOM kent geen weken; zeven dagen per week is exact.
      delen.d = (delen.d ?? 0) + aantal * 7;
    } else {
      delen[letter] = (delen[letter] ?? 0) + aantal;
    }
  }

  const uit = [
    delen.y !== undefined ? `${delen.y}y` : '',
    delen.m !== undefined ? `${delen.m}m` : '',
    delen.d !== undefined ? `${delen.d}d` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return uit || null;
}
